import React, { useState, useEffect } from 'react';
import { DollarSign, CreditCard, TrendingUp, Download, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import StatCard from '@/components/dashboard/StatCard';
import { useAuth } from '@/context/AuthContext';
import { listarPagamentosEmpresa, obterEmpresa } from '@/services/api';
import api from '@/services/api';

const FinanceiroCliente = () => {
  const { empresa, atualizarEmpresa } = useAuth();
  const [pagamentos, setPagamentos] = useState([]);
  const [recebiveis, setRecebiveis] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    carregarDados();
  }, []);
  
  const carregarDados = async () => {
    try {
      const [pagResponse, empResponse, recebiveisResponse] = await Promise.all([
        listarPagamentosEmpresa(empresa.id),
        obterEmpresa(empresa.id),
        api.get(`/recebiveis/cliente/${empresa.id}`)
      ]);
      
      setPagamentos(pagResponse.data);
      setRecebiveis(recebiveisResponse.data);
      atualizarEmpresa(empResponse.data);
    } catch (error) {
      toast.error('Erro ao carregar dados financeiros');
    } finally {
      setLoading(false);
    }
  };
  
  const getTipoBadge = (tipo) => {
    return tipo === 'assinatura' ? 
      <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">Assinatura</span> :
      <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">Créditos</span>;
  };
  
  const getStatusBadge = (status) => {
    const badges = {
      pendente: 'bg-amber-100 text-amber-800',
      aprovado: 'bg-green-100 text-green-800',
      rejeitado: 'bg-red-100 text-red-800',
      cancelado: 'bg-slate-100 text-slate-800'
    };
    return <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${badges[status]}`}>{status}</span>;
  };
  
  const totalGasto = pagamentos
    .filter(p => p.status === 'aprovado')
    .reduce((sum, p) => sum + p.valor, 0);
  
  const gastoAssinaturas = pagamentos
    .filter(p => p.status === 'aprovado' && p.tipo === 'assinatura')
    .reduce((sum, p) => sum + p.valor, 0);
  
  const gastoCreditos = pagamentos
    .filter(p => p.status === 'aprovado' && p.tipo === 'creditos')
    .reduce((sum, p) => sum + p.valor, 0);
  
  const totalRecebiveis = recebiveis
    .filter(r => r.status === 'pendente')
    .reduce((sum, r) => sum + r.valor, 0);
  
  const recebidosPlataforma = recebiveis
    .filter(r => r.status === 'recebido_plataforma')
    .reduce((sum, r) => sum + r.valor, 0);
  
  return (
    <DashboardLayout title="Controle Financeiro">
      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
        <StatCard
          title="Total Gasto"
          value={`R$ ${totalGasto.toFixed(2)}`}
          icon={DollarSign}
          color="blue"
        />
        <StatCard
          title="Assinaturas"
          value={`R$ ${gastoAssinaturas.toFixed(2)}`}
          icon={CreditCard}
          color="blue"
        />
        <StatCard
          title="Créditos Comprados"
          value={`R$ ${gastoCreditos.toFixed(2)}`}
          subtitle={`${empresa.creditos_total || 0} créditos`}
          icon={TrendingUp}
          color="emerald"
        />
        <StatCard
          title="A Receber"
          value={`R$ ${totalRecebiveis.toFixed(2)}`}
          subtitle={`${recebiveis.filter(r => r.status === 'pendente').length} pendentes`}
          icon={Clock}
          color="amber"
        />
        <StatCard
          title="Na Plataforma"
          value={`R$ ${recebidosPlataforma.toFixed(2)}`}
          subtitle="Aguardando repasse"
          icon={DollarSign}
          color="emerald"
        />
      </div>
      
      {/* Tabela de Recebíveis */}
      {recebiveis.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden mb-6">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Recebíveis (Valores a Receber)
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Data</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Entrega</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Valor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Recebido Por</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {recebiveis.map((rec) => (
                  <tr key={rec.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {new Date(rec.criado_em).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-slate-600">
                      {rec.entrega_id.substring(0, 8)}...
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-slate-900">
                      R$ {rec.valor.toFixed(2)}
                    </td>
                    <td className="px-6 py-4">
                      {rec.status === 'pendente' && (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                          Pendente
                        </span>
                      )}
                      {rec.status === 'recebido_plataforma' && (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                          Na Plataforma
                        </span>
                      )}
                      {rec.status === 'recebido_entregador' && (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                          Com Entregador
                        </span>
                      )}
                      {rec.status === 'repassado' && (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                          Repassado
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {rec.recebido_por === 'plataforma' ? 'Plataforma' : rec.recebido_por === 'entregador' ? 'Entregador' : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Tabela de Pagamentos */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden mb-6">
        <div className="p-6 border-b border-slate-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Histórico de Pagamentos
          </h2>
          <Button variant="outline" size="sm" className="gap-2">
            <Download className="w-4 h-4" />
            Exportar
          </Button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Data</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Descrição</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Valor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan="5" className="px-6 py-8 text-center text-slate-500">Carregando...</td></tr>
              ) : pagamentos.length === 0 ? (
                <tr><td colSpan="5" className="px-6 py-8 text-center text-slate-500">Nenhum pagamento realizado</td></tr>
              ) : (
                pagamentos.map((pag) => (
                  <tr key={pag.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {new Date(pag.criado_em).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-6 py-4">{getTipoBadge(pag.tipo)}</td>
                    <td className="px-6 py-4 text-sm text-slate-900">{pag.descricao}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-slate-900">
                      R$ {pag.valor.toFixed(2)}
                    </td>
                    <td className="px-6 py-4">{getStatusBadge(pag.status)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">Resumo do Plano</h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-blue-700 font-medium">Assinatura Mensal</p>
            <p className="text-blue-900 font-bold text-xl">R$ 149,90</p>
          </div>
          <div>
            <p className="text-blue-700 font-medium">Custo por Entrega</p>
            <p className="text-blue-900 font-bold text-xl">R$ 1,50</p>
          </div>
          <div>
            <p className="text-blue-700 font-medium">Status da Assinatura</p>
            <p className={`font-semibold ${empresa.assinatura_ativa ? 'text-green-700' : 'text-red-700'}`}>
              {empresa.assinatura_ativa ? 'Ativa' : 'Inativa'}
            </p>
          </div>
          <div>
            <p className="text-blue-700 font-medium">Vencimento</p>
            <p className="text-blue-900 font-semibold">
              {empresa.data_vencimento_assinatura ? 
                new Date(empresa.data_vencimento_assinatura).toLocaleDateString('pt-BR') : 
                'N/A'
              }
            </p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FinanceiroCliente;
